import { SYSTEM_COLUMN_IDS } from "@src/components/ui-kit/DataTable/Body/hooks/columns";
import type { DataTableBodyStore } from "@src/components/ui-kit/DataTable/Body/store";
import type { ColumnPinningState, Updater } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";

interface UseTableBodyPinningArgs {
  tanstackBodyStore: DataTableBodyStore;
  dataColumnIds: string[];
  dataColumnIdsSet: Set<string>;
}

export function useTableBodyPinning(args: UseTableBodyPinningArgs) {
  const { tanstackBodyStore, dataColumnIds, dataColumnIdsSet } = args;

  const filtersPinnableId = useCallback(
    (id: string) => dataColumnIdsSet.has(id) || SYSTEM_COLUMN_IDS.includes(id),
    [dataColumnIdsSet],
  );

  useEffect(() => {
    const allColumnIds = [...dataColumnIds, ...SYSTEM_COLUMN_IDS];

    tanstackBodyStore.syncPinnedColumnIds(allColumnIds);
  }, [dataColumnIds, tanstackBodyStore]);

  const columnPinning = useMemo(
    () => ({
      left: tanstackBodyStore.pinnedColumnIds.filter(filtersPinnableId),
      right: [],
    }),
    [tanstackBodyStore.pinnedColumnIds, filtersPinnableId],
  );

  const onColumnPinningChange = useCallback(
    (updater: Updater<ColumnPinningState>) => {
      const currentPinning: ColumnPinningState = {
        left: tanstackBodyStore.pinnedColumnIds.slice(),
        right: [],
      };

      const nextPinning =
        typeof updater === "function" ? updater(currentPinning) : updater;
      const nextLeftPinned = nextPinning.left
        ? nextPinning.left.filter(filtersPinnableId)
        : [];

      tanstackBodyStore.setPinnedColumnIds(nextLeftPinned);
    },
    [
      tanstackBodyStore.pinnedColumnIds,
      filtersPinnableId,
      tanstackBodyStore.setPinnedColumnIds,
    ],
  );

  return { columnPinning, onColumnPinningChange };
}
